'use client';

import { getSnapshot, type Instance, t } from 'mobx-state-tree';

/* Step when snapping is enabled */
// Snap value steps organized by state for clarity and extensibility
export const SnapStep = {
  ENABLED: { translate: 0.1, rotate: 10, scale: 0.01 } as const,
  DISABLED: { translate: 0, rotate: 1, scale: 0.01 } as const
};

/** SnapValues Model */
export const SnapValues = t
  .model('SnapValues', {
    translate: t.optional(t.number, SnapStep.ENABLED.translate),
    rotate: t.optional(t.number, SnapStep.ENABLED.rotate),
    scale: t.optional(t.number, SnapStep.ENABLED.scale),
    translateEnabled: t.optional(t.boolean, true),
    rotateEnabled: t.optional(t.boolean, true),
    scaleEnabled: t.optional(t.boolean, true)
  })
  .views(self => ({
    get values() { return getSnapshot(self); },
    get translateStep() { return self.translateEnabled ? self.translate : SnapStep.DISABLED.translate; },
    get rotateStep() { return self.rotateEnabled ? self.rotate : SnapStep.DISABLED.rotate; },
    get scaleStep() { return self.scaleEnabled ? self.scale : SnapStep.DISABLED.scale; },
    /** Returns true if any snapping is currently enabled */
    get anyEnabled() { return self.translateEnabled || self.rotateEnabled || self.scaleEnabled; }
  }))
  .actions(self => ({
    update(values: Partial<typeof self>) {
      Object.entries(values).forEach(([key, value]) => {
        if (key in self && value !== undefined) {
          (self as any)[key] = value;
        }
      });
    },
    /** Toggle all snapping on/off - turns all on if any is off, turns all off if all are on */
    toggleAll() {
      const newState = !self.anyEnabled;
      self.translateEnabled = newState;
      self.rotateEnabled = newState;
      self.scaleEnabled = newState;
    },
    /** Reset all snap values to their default values */
    reset() {
      self.translate = SnapStep.ENABLED.translate;
      self.rotate = SnapStep.ENABLED.rotate;
      self.scale = SnapStep.ENABLED.scale;
      self.translateEnabled = true;
      self.rotateEnabled = true;
      self.scaleEnabled = true;
    }
  }));

export interface ISnapValues extends Instance<typeof SnapValues> { }